"use client";

import { useState } from "react";
import SafeImage from "@/components/SafeImage";
import Link from "next/link";
import { ArrowRight, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./Blog.module.css";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  authorName: string;
  authorAvatar: string;
  image: string;
  slug?: string;
}

interface BlogProps {
  initialPosts: BlogPost[];
}

const POSTS_PER_PAGE = 6;

export default function Blog({ initialPosts }: BlogProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(initialPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const currentPosts = initialPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handlePageSelect = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <section className={styles.blogSection} id="blog">
      <div className={`${styles.container} container`}>
        {/* Header Title */}
        <div className={styles.header}>
          <span className="badge badge-purple">Hotel News & Insights</span>
          <h2 className={styles.title}>Explore Our Blog</h2>
          <p className={styles.subtitle}>
            Stay ahead in the hospitality industry with our curated insights, hotel management strategies, and tips to maximize revenue.
          </p>
        </div>

        {/* Blog Post Grid */}
        <div className={styles.grid}>
          {currentPosts.map((post) => (
            <article key={post.id} className={styles.card}>
              <div className={styles.imageWrapper}>
                <SafeImage
                  src={post.image}
                  alt={post.title}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className={styles.image}
                />
                <span className={styles.categoryBadge}>{post.category}</span>
              </div>

              <div className={styles.content}>
                <span className={styles.date}>{post.date}</span>
                <h3 className={styles.cardTitle}>
                  <Link href={`/blog/${post.slug}`}>{post.title}</Link>
                </h3>
                <p className={styles.excerpt}>{post.excerpt}</p>
                
                <Link href={`/blog/${post.slug}`} className={styles.readMore}>
                  Read Full Article <ArrowRight size={14} />
                </Link>
              </div>

              <div className={styles.footer}>
                <div className={styles.author}>
                  <div className={styles.avatarWrapper}>
                    <img
                      src={post.authorAvatar}
                      alt={post.authorName}
                      width={28}
                      height={28}
                      className={styles.avatar}
                    />
                  </div>
                  <span className={styles.authorName}>{post.authorName}</span>
                </div>
                <div className={styles.readTime}>
                  <Clock size={12} />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div className={styles.pageNumbers}>
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    className={`${styles.pageNumber} ${currentPage === page ? styles.activePage : ""}`}
                    onClick={() => handlePageSelect(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              className={styles.pageButton}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
